import React, { Suspense, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Button, Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { isSignupEnabled } from '../../config/features';
import BrandLogo from '../BrandLogo/BrandLogo';
import { isChromelessPath } from './chromelessPaths';
import { loadAuthenticatedNavigation } from './loadAuthenticatedNavigation';

/**
 * The signed-in chrome (sidebar, role menus, notification bell, breadcrumbs, profile menu) is a
 * sizeable chunk of MUI and menu configuration that anonymous visitors never use, so it is loaded
 * on demand and the landing page ships only the small public app bar below.
 */
type NavigationComponent = React.ComponentType;

/** In-page anchors of the landing page, in the order of the client mockup's header. */
const LANDING_ANCHORS = [
  { id: 'programa', key: 'program' },
  { id: 'percurso', key: 'journey' },
  { id: 'beneficios', key: 'benefits' },
  { id: 'perguntas', key: 'faq' },
] as const;

/**
 * App bar for visitors who are not signed in. On the landing page it follows the client mockup
 * (section anchors and the "talk about my team" call to action, with a discreet Login); elsewhere it
 * shows About, Login and optionally Sign Up.
 */
const PublicNavigation: React.FC<{ showLogo: boolean }> = ({ showLogo }) => {
  const { t } = useTranslation(['common', 'menu', 'landing']);
  const navigate = useNavigate();
  const theme = useTheme();
  const onLanding = useLocation().pathname === '/';

  const publicButtonSx = {
    color: 'primary.main',
    borderColor: alpha(theme.palette.primary.main, 0.5),
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    minWidth: 110,
    '&:hover': {
      borderColor: 'primary.main',
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: 'background.paper',
        backdropFilter: 'blur(10px)',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'divider',
        boxShadow: 'none',
      }}
    >
      <Toolbar sx={{ px: { xs: 2, md: 4 } }}>
        {showLogo && (
          <Box component={RouterLink} to="/" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <BrandLogo height={44} />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center' }}>
          {onLanding ? (
            <>
              <Box
                component="nav"
                aria-label={t('landing:nav.label', 'Sections')}
                sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}
              >
                {LANDING_ANCHORS.map(({ id, key }) => (
                  <Button key={id} href={`#${id}`} color="inherit" sx={{ color: 'text.primary' }}>
                    {t(`landing:nav.${key}`)}
                  </Button>
                ))}
              </Box>
              <Button component={RouterLink} to="/login" color="inherit" sx={{ color: 'text.primary' }}>
                {t('common:navigation.login', 'Login')}
              </Button>
              <Button
                href="#contacto"
                variant="contained"
                size="small"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                {t('landing:nav.cta')}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => navigate('/about')}
                color="inherit"
                sx={{ color: 'text.primary', display: { xs: 'none', sm: 'inline-flex' } }}
              >
                {t('common:navigation.about', 'About')}
              </Button>
              <Button
                component={RouterLink}
                to="/login"
                variant="outlined"
                color="primary"
                size="small"
                sx={publicButtonSx}
              >
                {t('common:navigation.login', 'Login')}
              </Button>
            </>
          )}
          {isSignupEnabled && (
            <Button
              component={RouterLink}
              to="/signup"
              variant="outlined"
              color="primary"
              size="small"
              sx={publicButtonSx}
            >
              {t('common:navigation.signup', 'Sign Up')}
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

/** Width of the permanent sidebar; keep in sync with AuthenticatedNavigation's DRAWER_WIDTH. */
const SIDEBAR_PLACEHOLDER_WIDTH = 240;

/**
 * Stands in for the signed-in chrome while its chunk loads: it reserves the sidebar's space (so the
 * page does not jump sideways), and it already shows the logo and the bar outline where the real
 * ones will appear, so the first paint looks like the finished page and the logo (often the largest
 * element on a light page) is not first painted late.
 */
const NavigationPlaceholder: React.FC = () => (
  <>
    <Box
      aria-hidden
      data-testid="navigation-placeholder"
      sx={{ width: SIDEBAR_PLACEHOLDER_WIDTH, flexShrink: 0, display: { xs: 'none', md: 'block' } }}
    >
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: 0,
          width: SIDEBAR_PLACEHOLDER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <BrandLogo height={32} />
        </Box>
      </Box>
    </Box>
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        left: { xs: 0, md: SIDEBAR_PLACEHOLDER_WIDTH },
        height: 64,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    />
  </>
);

interface NavigationProps {
  /**
   * False until the page content has rendered. The signed-in chrome (a dozen requests of MUI and
   * icons) is only requested once this is true, so on a slow connection it never delays the content
   * the user came for. Defaults to true.
   */
  chromeReady?: boolean;
}

export const Navigation: React.FC<NavigationProps> = ({ chromeReady = true }) => {
  const { isAuthenticated, hasActiveRole } = useAuth();
  const location = useLocation();
  const wantsChrome = isAuthenticated && hasActiveRole && chromeReady && !isChromelessPath(location.pathname);
  // The chunk is imported by hand rather than through React.lazy: a lazy component shows a Suspense
  // fallback, and React holds back any other pending reveal (the page's own content) for 300 ms
  // after a fallback is committed. The placeholder below does the same job without that cost.
  const [Chrome, setChrome] = useState<NavigationComponent | null>(null);

  useEffect(() => {
    if (!wantsChrome || Chrome) return undefined;
    let cancelled = false;
    loadAuthenticatedNavigation().then(
      (module) => {
        if (!cancelled) setChrome(() => module.default);
      },
      () => undefined, // a failed download leaves the placeholder; the next visit retries
    );
    return () => {
      cancelled = true;
    };
  }, [wantsChrome, Chrome]);

  if (isChromelessPath(location.pathname)) {
    return null;
  }

  if (isAuthenticated && hasActiveRole) {
    return Chrome && chromeReady ? (
      <Suspense fallback={<NavigationPlaceholder />}>
        <Chrome />
      </Suspense>
    ) : (
      <NavigationPlaceholder />
    );
  }

  return <PublicNavigation showLogo={!isAuthenticated} />;
};

export default Navigation;
